import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './select.module.scss';

const styles = pickCss(css, css.root);

type SelectSize = 'compact' | 'sm' | 'default' | 'lg';
type SelectDropdownPosition = 'top' | 'bottom';

export type SelectOption = {
  label: string;
  content?: ReactNode;
  value: string;
  // Extra terms the `searchable` filter matches in addition to `label` (never
  // displayed). Lets an option labelled "(GMT+05:30) Asia/Kolkata" carry
  // "india ist new delhi" so those queries surface it.
  keywords?: string;
};

type SelectProps = {
  'aria-label'?: string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  dropdownClassName?: string;
  dropdownMaxHeight?: CSSProperties['maxHeight'];
  dropdownMaxWidth?: CSSProperties['maxWidth'];
  dropdownMinWidth?: CSSProperties['minWidth'];
  dropdownPosition?: SelectDropdownPosition;
  dropdownWidth?: CSSProperties['width'];
  // Render only the leadingIcon + chevron in the trigger (hide the selected
  // label) — for compact swatch-style selects.
  hideValue?: boolean;
  id?: string;
  leadingIcon?: ReactNode;
  name?: string;
  onChange?: (e: { target: { value: string } }) => void;
  // `readonly` so `as const` option tables (e.g. COUNTRY_OPTIONS) pass without
  // a defensive copy or cast — the list is only ever read and mapped here.
  options?: readonly SelectOption[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  size?: SelectSize;
  style?: CSSProperties;
  // Pinned content under the option list (e.g. the pricing table's
  // "Manage room types" action). Stays visible while searching because it
  // is not part of the filtered options. The render-prop form receives a
  // close() callback so footer actions can collapse the dropdown.
  footer?: ReactNode | ((close: () => void) => ReactNode);
  // Native tooltip on the trigger — used where the selected label can
  // truncate (e.g. long room type names in the pricing table).
  title?: string;
  usePortal?: boolean;
  value?: string;
};

type PortalPosition = {
  top: number;
  left: number;
  right: number;
  width: number;
  placement: SelectDropdownPosition;
  // Anchor the dropdown's right edge to the trigger (open leftward) when a
  // left-aligned panel would overflow the viewport's right edge.
  alignRight: boolean;
};

const PORTAL_DROPDOWN_GAP = 4;
const PORTAL_DROPDOWN_MAX_HEIGHT = 260;
const PORTAL_DROPDOWN_EDGE_MARGIN = 8;

export const Select = ({
  'aria-label': ariaLabel,
  children,
  className,
  disabled,
  dropdownClassName,
  dropdownMaxHeight,
  dropdownMaxWidth,
  dropdownMinWidth,
  dropdownPosition = 'bottom',
  dropdownWidth,
  hideValue = false,
  id,
  leadingIcon,
  name,
  onChange,
  options,
  placeholder,
  footer,
  searchable = false,
  title,
  searchPlaceholder = 'Search…',
  size = 'default',
  style,
  usePortal = false,
  value,
}: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  // Type-ahead buffer for keyboard matching on the trigger (e.g. typing "21"
  // in the hour picker jumps straight to 21). Reset after a short idle pause.
  const typeaheadRef = useRef<{ buffer: string; timer: number }>({
    buffer: '',
    timer: 0,
  });
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(
    null
  );

  // Build options from either `options` prop or `<option>` children
  const items: readonly SelectOption[] =
    options ?? extractOptionsFromChildren(children);

  const visibleItems = useMemo(() => {
    if (!searchable) return items;
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.keywords?.toLowerCase().includes(q) ?? false)
    );
  }, [items, searchable, query]);

  const selected = items.find((o) => o.value === value);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.({ target: { value: optionValue } });
      setOpen(false);
    },
    [onChange]
  );

  // Keyboard type-ahead on the trigger: printable keys accumulate into a
  // buffer (so "2" then "1" matches "21", not just "1"), which is matched
  // against option labels. Matching selects the option immediately and scrolls
  // it into view if the dropdown is open. Disabled while `searchable` (the
  // search input owns typing then). Skipped for modifier combos so shortcuts
  // like ⌘C still work.
  const handleTriggerKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (searchable) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;

      const state = typeaheadRef.current;
      window.clearTimeout(state.timer);
      const nextBuffer = state.buffer + e.key;
      state.buffer = nextBuffer;
      state.timer = window.setTimeout(() => {
        typeaheadRef.current.buffer = '';
      }, 800);

      const q = nextBuffer.toLowerCase();
      // Prefer a prefix match; on a longer buffer that no longer prefixes any
      // label (e.g. two unrelated keys), fall back to the last char alone so a
      // fresh single keystroke still jumps.
      const byPrefix = (term: string) =>
        items.find((o) => o.label.toLowerCase().startsWith(term));
      const match = byPrefix(q) ?? byPrefix(e.key.toLowerCase());
      if (!match) return;

      e.preventDefault();
      if (match.value !== value) {
        onChange?.({ target: { value: match.value } });
      }
    },
    [items, onChange, searchable, value]
  );

  // Reset + focus search when opening
  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    if (!searchable) return undefined;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, searchable]);

  // Keep the selected option in view when the dropdown opens and as type-ahead
  // moves the selection (e.g. scrolling from "00" to "21" in the hour picker).
  useEffect(() => {
    if (!open) return;
    // Wait a tick so the portal dropdown has mounted before measuring.
    const t = window.setTimeout(() => {
      selectedOptionRef.current?.scrollIntoView({ block: 'nearest' });
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, value]);

  // Clear any pending type-ahead reset timer on unmount.
  useEffect(() => () => window.clearTimeout(typeaheadRef.current.timer), []);

  // Close on outside click (also checks portal node)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePortal = portalRef.current?.contains(target);
      if (!insideRoot && !insidePortal) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Portal: compute trigger-relative position; recompute on scroll / resize
  useLayoutEffect(() => {
    if (!open || !usePortal) {
      setPortalPosition(null);
      return undefined;
    }
    const computePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement: SelectDropdownPosition =
        dropdownPosition === 'top' ||
        (spaceBelow < PORTAL_DROPDOWN_MAX_HEIGHT + PORTAL_DROPDOWN_GAP &&
          spaceAbove > spaceBelow)
          ? 'top'
          : 'bottom';
      const top =
        placement === 'top'
          ? rect.top - PORTAL_DROPDOWN_GAP
          : rect.bottom + PORTAL_DROPDOWN_GAP;
      // Decide whether a left-anchored panel would overflow the viewport's
      // right edge. The panel sizes to `max-content` floored at the trigger
      // width (see renderPortalDropdown) and is hard-capped by the
      // stylesheet's `max-width: min(360px, 100vw - 16px)`, so it can never
      // overflow the viewport regardless. The only reason to right-anchor is
      // when the trigger itself sits far enough right that a panel matching
      // its width would spill — so base the estimate on the trigger width.
      // (The previous 220px floor mis-flagged the 54px hour/minute time
      // selects near a modal's right edge, rendering their options
      // right-shifted/clipped.)
      const estWidth = rect.width;
      const alignRight =
        rect.left + estWidth > window.innerWidth - PORTAL_DROPDOWN_EDGE_MARGIN;
      setPortalPosition({
        top,
        left: rect.left,
        right: window.innerWidth - rect.right,
        width: rect.width,
        placement,
        alignRight,
      });
    };
    computePosition();
    const handleScroll = () => computePosition();
    const handleResize = () => computePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, usePortal, dropdownPosition]);

  const dropdownContent = (
    <>
      {searchable && (
        <div className={css.searchRow}>
          <Search className={css.searchIcon} />
          <input
            className={css.searchInput}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            ref={searchInputRef}
            type="text"
            value={query}
          />
        </div>
      )}
      <div className={css.optionList}>
        {visibleItems.map((option) => {
          const isSelected = option.value === value;
          return (
            <button
              className={joinCss(css.option, isSelected && css.optionSelected)}
              key={option.value}
              onClick={() => handleSelect(option.value)}
              ref={isSelected ? selectedOptionRef : undefined}
              type="button"
            >
              <span className={css.checkSlot}>
                {isSelected && <Check className={css.checkIcon} />}
              </span>
              <span className={css.optionLabel}>
                {option.content ?? option.label}
              </span>
            </button>
          );
        })}
        {visibleItems.length === 0 && (
          <span className={css.empty}>No options</span>
        )}
      </div>
      {footer && (
        <div className={css.dropdownFooter}>
          {typeof footer === 'function' ? footer(() => setOpen(false)) : footer}
        </div>
      )}
    </>
  );

  const renderInlineDropdown = () => (
    <div
      className={joinCss(
        css.dropdown,
        dropdownClassName,
        size === 'compact' && css.dropdownCompact,
        dropdownPosition === 'top' && css.dropdownTop
      )}
      style={{
        maxHeight: dropdownMaxHeight,
        maxWidth: dropdownMaxWidth,
        minWidth: dropdownMinWidth,
        width: dropdownWidth,
      }}
    >
      {dropdownContent}
    </div>
  );

  const renderPortalDropdown = () => {
    if (typeof document === 'undefined') return null;
    if (!portalPosition) return null;
    const portalStyle: CSSProperties = {
      position: 'fixed',
      maxHeight: dropdownMaxHeight,
      maxWidth: dropdownMaxWidth,
      // Floor at the trigger width so the panel is never narrower than its
      // anchor, but DON'T pin `width` to it — a narrow trigger (e.g. the
      // 54px hour/minute time selects) would otherwise crush each option's
      // label to a sliver. Let `width` fall to the stylesheet's
      // `max-content` so the panel grows to fit `check + label`, exactly like
      // the inline dropdown. An explicit `dropdownWidth`/`dropdownMinWidth`
      // still wins for callers that want a fixed-width panel.
      minWidth: dropdownMinWidth ?? portalPosition.width,
      width: dropdownWidth,
      ...(portalPosition.alignRight
        ? { right: portalPosition.right }
        : { left: portalPosition.left }),
      ...(portalPosition.placement === 'top'
        ? { bottom: window.innerHeight - portalPosition.top }
        : { top: portalPosition.top }),
    };
    return createPortal(
      <div
        className={joinCss(
          css.dropdown,
          css.dropdownPortal,
          dropdownClassName,
          size === 'compact' && css.dropdownCompact,
          portalPosition.placement === 'top' && css.dropdownTop
        )}
        data-select-portal="true"
        ref={portalRef}
        style={portalStyle}
      >
        {dropdownContent}
      </div>,
      document.body
    );
  };

  return (
    <div className={css.wrapper} ref={rootRef} style={style}>
      <button
        className={styles(
          { size, isDisabled: !!disabled, isOpen: open },
          className
        )}
        aria-label={ariaLabel}
        disabled={disabled}
        id={id}
        name={name}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        ref={triggerRef}
        title={title}
        type="button"
      >
        {leadingIcon && <span className={css.icon}>{leadingIcon}</span>}
        {!hideValue && (
          <span className={selected ? css.value : css.placeholder}>
            {selected?.content ?? selected?.label ?? placeholder ?? ' '}
          </span>
        )}
        <ChevronDown
          className={joinCss(css.chevron, open && css.chevronOpen)}
        />
      </button>

      {open && (usePortal ? renderPortalDropdown() : renderInlineDropdown())}
    </div>
  );
};

/**
 * Extract options from `<option>` children for backwards compatibility.
 */
const childrenToString = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(childrenToString).join('');
  return '';
};

const extractOptionsFromChildren = (children: ReactNode): SelectOption[] => {
  const result: SelectOption[] = [];
  const flat = Array.isArray(children) ? children.flat(2) : [children];

  for (const child of flat) {
    if (
      child &&
      typeof child === 'object' &&
      'props' in child &&
      child.type === 'option'
    ) {
      const props = child.props as { value?: string; children?: ReactNode };
      result.push({
        value: String(props.value ?? ''),
        label: childrenToString(props.children ?? props.value ?? ''),
      });
    }
  }

  return result;
};
